import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/Card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/Accordion';
import { Progress } from './components/Progress';
import { Badge } from './components/Badge';
import { Check, Star, Gift, ArrowRight, Sparkles, BookOpen, Clock, Target, Users, ShieldCheck, ChevronRight, Download, Infinity as InfinityIcon, Lock } from 'lucide-react';
import { TestimonialsMarquee } from './components/TestimonialsMarquee';
export function App() {
  return <div className="min-h-screen w-full bg-background text-foreground selection:bg-primary/30 selection:text-primary-foreground antialiased" data-id="element-0">
      {/* 1. Sticky top nav */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md" data-id="element-1">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-2">
          <div className="font-heading font-bold text-xl tracking-widest text-primary" data-id="element-3">
            COSMOS™
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-muted-foreground" data-id="element-4">
            <a href="#tomos" className="hover:text-primary transition-colors" data-id="element-5">
              Tomos
            </a>
            <a href="#bonus" className="hover:text-primary transition-colors" data-id="element-6">
              Bonus
            </a>
            <a href="#testimonios" className="hover:text-primary transition-colors" data-id="element-7">
              Testimonios
            </a>
            <a href="#faq" className="hover:text-primary transition-colors" data-id="element-8">
              FAQ
            </a>
          </div>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_0_15px_rgba(212,168,87,0.3)]" data-id="element-9">
            Acceder Ahora
          </Button>
        </div>
      </nav>

      {/* 2. Hero */}
      <section className="relative pt-24 pb-32 overflow-hidden" data-id="element-10">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-11">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl" data-id="element-12"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-background/50 to-background" data-id="element-13"></div>
          {/* Subtle star dots */}
          <div className="absolute top-20 left-20 w-1 h-1 bg-primary/40 rounded-full shadow-[0_0_10px_rgba(212,168,87,0.8)]" data-id="element-14"></div>
          <div className="absolute top-40 right-32 w-1.5 h-1.5 bg-primary/30 rounded-full shadow-[0_0_12px_rgba(212,168,87,0.6)]" data-id="element-15"></div>
          <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-primary/50 rounded-full shadow-[0_0_8px_rgba(212,168,87,0.9)]" data-id="element-16"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 max-w-4xl text-center" data-id="element-17">
          <Badge variant="outline" className="mb-8 border-primary/50 text-primary uppercase tracking-widest px-4 py-1.5 text-xs font-semibold bg-primary/5 backdrop-blur-sm" data-id="element-18">
            EL SISTEMA QUE ORGANIZA TU ASTROLOGÍA
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.1] mb-4 text-foreground" data-id="element-19">
            No necesitas más astrología.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-20">
              Necesitas estructura.
            </span>
          </h1>

          {/* Tomos image directly below the main title */}
          <motion.div className="relative flex justify-center mb-8 -mt-2" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-21">
            <div className="absolute inset-0 bg-primary/10 rounded-full blur-3xl scale-75 pointer-events-none" data-id="element-22"></div>
            <motion.img src="https://cdn.magicpatterns.com/uploads/o81KwjDAxaxdFVtUR7J4e6/COSMOS_COMPLETOS_SIN_FONDOS.png" alt="Colección COSMOS™ — 5 tomos profesionales de astrología" className="relative z-10 w-full max-w-2xl lg:max-w-3xl drop-shadow-[0_20px_50px_rgba(212,168,87,0.3)]" animate={{
            y: [0, -12, 0]
          }} transition={{
            duration: 6,
            ease: 'easeInOut',
            repeat: Infinity
          }} data-id="element-23" />
          </motion.div>

          {/* Trust badges — directly below the manual image */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-8 text-sm font-medium text-foreground/80" data-id="element-24">
            {[{
            icon: Download,
            label: 'Descarga inmediata'
          }, {
            icon: InfinityIcon,
            label: 'Acceso de por vida'
          }, {
            icon: Lock,
            label: 'Pago seguro'
          }, {
            icon: ShieldCheck,
            label: 'Garantía 7 días'
          }].map((b, i) => <div key={i} className="flex items-center gap-2" data-id="element-25">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 border border-primary/20" data-id="element-26">
                  <b.icon className="w-3.5 h-3.5 text-primary" data-id="element-27" />
                </div>
                <span data-id="element-28">{b.label}</span>
              </div>)}
          </div>

          {/* Urgency chips — directly below the manual image */}
          <div className="flex flex-col items-center gap-3 mb-10 text-sm md:text-base font-medium" data-id="element-29">
            <div className="flex items-center gap-2 text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20" data-id="element-30">
              <span data-id="element-31">🔥</span> 5 TOMOS + 10 BONUS SOLO HOY
            </div>
            <div className="flex items-center gap-2 text-muted-foreground" data-id="element-32">
              <span data-id="element-33">⏰</span> Oferta especial por tiempo limitado
            </div>
            <div className="flex items-center gap-2 text-amber-500/90" data-id="element-34">
              <span data-id="element-35">⚠️</span> Precio promocional disponible hoy
            </div>
          </div>

          <p className="text-lg md:text-xl text-foreground/85 mb-4 leading-relaxed max-w-2xl mx-auto" data-id="element-36">
            El sistema que organiza tus conocimientos y transforma tus
            interpretaciones.
          </p>

          <p className="text-lg md:text-xl text-primary/90 italic font-medium mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-37">
            Abrí cualquier carta natal y sabé exactamente qué hacer.
          </p>

          <p className="text-base md:text-lg text-muted-foreground mb-8 font-medium" data-id="element-38">
            Colección Profesional COSMOS™ — Sistema PRO de Cartas Natales
          </p>

          <p className="text-base text-muted-foreground/90 leading-relaxed max-w-2xl mx-auto mb-12" data-id="element-39">
            La colección definitiva creada para astrólogas y futuras astrólogas
            que quieren dejar de sentirse bloqueadas y aprender un método claro
            para interpretar cualquier carta natal.
          </p>

          {/* Chips */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-40">
            {['COSMOS I: Orden Natal', 'COSMOS II: Conexión Natal', 'COSMOS III: Fluidez Natal', 'COSMOS IV: Sesiones Natales', 'COSMOS V: Astróloga Profesional', 'Bonus exclusivos'].map((chip, i) => <span key={i} className="px-3 py-1.5 bg-card border border-border rounded-md text-sm text-foreground/80 shadow-sm" data-id="element-41">
                {chip}
              </span>)}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-42">
            <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(212,168,87,0.4)] transition-all hover:scale-105" data-id="element-43">
              QUIERO ACCEDER AHORA <ArrowRight className="ml-2 h-5 w-5" data-id="element-44" />
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto text-lg px-8 py-6 h-auto border-primary/30 text-primary hover:bg-primary/10" data-id="element-45">
              Ver contenido completo
            </Button>
          </div>
        </div>
      </section>

      {/* 3. ¿Qué incluye la colección? */}
      <section id="tomos" className="py-24 bg-card/30 border-y border-border/50 relative" data-id="element-46">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-47">
          <div className="text-center mb-16" data-id="element-48">
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6" data-id="element-49">
              ¿Qué incluye la colección?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-id="element-50">
              Cinco tomos estructurados para llevarte desde el caos mental hasta
              una lectura profesional clara y organizada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-51">
            {[{
            num: 'I',
            title: 'Orden Natal',
            desc: 'Aprende exactamente qué mirar primero.',
            image: 'https://cdn.magicpatterns.com/uploads/5f29cmtmrM6dJVdGjCQCQk/1.png',
            keywords: ['Orden mental', 'Prioridades', 'Estructura', 'Interpretación', 'Método COSMOS']
          }, {
            num: 'II',
            title: 'Conexión Natal',
            desc: 'Aprende a unir todas las piezas.',
            image: 'https://cdn.magicpatterns.com/uploads/mH7sisxyi4vRwKWqUekW9n/2.png',
            keywords: ['Planetas', 'Casas', 'Aspectos', 'Patrones', 'Narrativas']
          }, {
            num: 'III',
            title: 'Fluidez Natal',
            desc: 'Interpreta con rapidez y seguridad.',
            image: 'https://cdn.magicpatterns.com/uploads/6oe63Qav8ewUyXSqtgUqSk/3.png',
            keywords: ['Lectura rápida', 'Reducción de tiempo', 'Métodos visuales', 'Sistemas']
          }, {
            num: 'IV',
            title: 'Sesiones Natales',
            desc: 'Transforma una lectura en una experiencia profesional.',
            image: 'https://cdn.magicpatterns.com/uploads/h2c1uJt2knZjnZYHpoWSC3/4.png',
            keywords: ['Estructura de sesión', 'Conexión con clientes', 'Comunicación', 'Guiones']
          }, {
            num: 'V',
            title: 'Astróloga Profesional',
            desc: 'Convierte conocimiento en un servicio real.',
            image: 'https://cdn.magicpatterns.com/uploads/aDTMCAwwfc8icFop99Bh11/5.png',
            keywords: ['Precios', 'Clientes', 'Posicionamiento', 'Profesionalización']
          }].map((tomo, i) => <Card key={i} className="bg-card border-border/50 hover:border-primary/50 transition-colors group relative overflow-hidden" data-id="element-52">
                <div className="absolute -right-6 -top-6 text-9xl font-heading font-black text-primary/5 group-hover:text-primary/10 transition-colors pointer-events-none select-none" data-id="element-53">
                  {tomo.num}
                </div>

                {/* Book cover image */}
                <div className="relative z-10 flex justify-center items-end pt-8 pb-2 px-4 h-64 overflow-hidden" data-id="element-54">
                  <div className="absolute inset-x-8 bottom-4 h-32 bg-primary/15 blur-2xl rounded-full pointer-events-none" data-id="element-55"></div>
                  <motion.img src={tomo.image} alt={`COSMOS ${tomo.num} — ${tomo.title}`} className="relative z-10 h-full w-auto object-contain drop-shadow-[0_15px_25px_rgba(0,0,0,0.5)]" whileHover={{
                y: -6,
                rotate: -1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} data-id="element-56" />
                </div>

                <CardHeader className="relative z-10 pb-4" data-id="element-57">
                  <div className="text-primary font-heading font-bold tracking-widest text-sm mb-2" data-id="element-58">
                    COSMOS {tomo.num}
                  </div>
                  <CardTitle className="text-2xl font-heading mb-2" data-id="element-59">
                    {tomo.title}
                  </CardTitle>
                  <CardDescription className="text-base text-foreground/80" data-id="element-60">
                    {tomo.desc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative z-10" data-id="element-61">
                  <ul className="space-y-2" data-id="element-62">
                    {tomo.keywords.map((kw, j) => <li key={j} className="flex items-center text-sm text-muted-foreground" data-id="element-63">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/60 mr-2" data-id="element-64"></div>
                        {kw}
                      </li>)}
                  </ul>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. BONUS EXCLUSIVOS */}
      <section id="bonus" className="py-24 relative" data-id="element-65">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-66">
          <div className="text-center mb-16" data-id="element-67">
            <Badge className="bg-primary/20 text-primary hover:bg-primary/30 mb-4" data-id="element-68">
              SOLO POR HOY
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6" data-id="element-69">
              BONUS EXCLUSIVOS
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-id="element-70">
              Herramientas prácticas que aceleran resultados y simplifican tu
              trabajo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-71">
            {[{
            title: 'Atlas de Casas Astrológicas PRO',
            desc: 'Una guía visual para entender rápidamente las 12 casas y sus conexiones principales.'
          }, {
            title: 'Guía Express de Aspectos Planetarios',
            desc: 'Referencia rápida para interpretar aspectos sin perder tiempo buscando información.'
          }, {
            title: 'Biblioteca de Frases Profesionales',
            desc: 'Más de 200 ejemplos de frases listas para usar durante sesiones reales.'
          }, {
            title: 'Plantillas PDF para Clientes',
            desc: 'Diseños listos para entregar lecturas profesionales y mejorar la experiencia.'
          }, {
            title: 'Sistema de Interpretación en 10 Minutos',
            desc: 'Método visual para detectar los puntos más importantes de cualquier carta.'
          }, {
            title: 'Guía de Bloqueos y Patrones Repetitivos',
            desc: 'Aprende a detectar patrones emocionales y energéticos rápidamente.'
          }, {
            title: 'Diccionario Cósmico PRO',
            desc: 'Glosario completo con términos simplificados para consultas rápidas.'
          }, {
            title: 'Guía de Combinaciones Poderosas',
            desc: 'Interpretaciones de combinaciones frecuentes entre signos, casas y planetas.'
          }, {
            title: 'Pack de Recursos para Redes Sociales',
            desc: 'Ideas y plantillas listas para publicar contenido como astróloga profesional.'
          }, {
            title: 'Mapa Visual Completo del Método COSMOS',
            desc: 'Todo el sistema resumido en una sola página visual fácil de imprimir.'
          }].map((bonus, i) => <div key={i} className="flex items-start p-6 bg-card border border-border/50 rounded-xl hover:bg-card/80 transition-colors" data-id="element-72">
                <div className="text-2xl mr-4 mt-1 bg-primary/10 p-3 rounded-lg border border-primary/20" data-id="element-73">
                  🎁
                </div>
                <div data-id="element-74">
                  <h3 className="font-heading font-bold text-lg mb-1 text-foreground" data-id="element-75">
                    {bonus.title}
                  </h3>
                  <p className="text-muted-foreground" data-id="element-76">{bonus.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. ¿Para quién es esta colección? */}
      <section className="py-24 bg-card/30 border-y border-border/50" data-id="element-77">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-78">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-12 text-center" data-id="element-79">
            ¿Para quién es esta colección?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-80">
            {['Astrólogas', 'Futuras astrólogas', 'Personas que estudian astrología', 'Personas que quieren profesionalizarse', 'Personas que se sienten bloqueadas', 'Personas que desean orden y claridad'].map((item, i) => <div key={i} className="flex items-center p-4 bg-background border border-border rounded-lg shadow-sm" data-id="element-81">
                <div className="bg-primary/20 p-1.5 rounded-full mr-4" data-id="element-82">
                  <Check className="w-5 h-5 text-primary" data-id="element-83" />
                </div>
                <span className="font-medium text-foreground/90" data-id="element-84">{item}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. Lo que aprenderás */}
      <section className="py-24 relative" data-id="element-85">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-86">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-16 text-center" data-id="element-87">
            Lo que aprenderás
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-88">
            {[{
            step: 1,
            title: 'Aprender qué mirar primero',
            desc: 'Conoce el orden correcto.',
            icon: Target
          }, {
            step: 2,
            title: 'Organizar información',
            desc: 'Transforma caos en estructura.',
            icon: BookOpen
          }, {
            step: 3,
            title: 'Conectar información',
            desc: 'Une piezas dispersas.',
            icon: Sparkles
          }, {
            step: 4,
            title: 'Interpretar historias',
            desc: 'Deja de leer símbolos aislados.',
            icon: Users
          }, {
            step: 5,
            title: 'Entregar sesiones',
            desc: 'Aprende una estructura profesional.',
            icon: Clock
          }, {
            step: 6,
            title: 'Trabajar con seguridad',
            desc: 'Interpreta con claridad y confianza.',
            icon: ShieldCheck
          }].map((item, i) => <div key={i} className="relative p-8 bg-card border border-border/50 rounded-2xl hover:border-primary/30 transition-all" data-id="element-89">
                <div className="absolute -top-5 left-8 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md" data-id="element-90">
                  Paso {item.step}
                </div>
                <item.icon className="w-10 h-10 text-primary/80 mb-6" data-id="element-91" />
                <h3 className="font-heading font-bold text-xl mb-3" data-id="element-92">
                  {item.title}
                </h3>
                <p className="text-muted-foreground" data-id="element-93">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Resultados que podés obtener */}
      <section className="py-20 bg-primary/5 border-y border-primary/10" data-id="element-94">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-95">
          <h2 className="text-2xl md:text-3xl font-heading font-bold mb-12 text-center text-foreground/90" data-id="element-96">
            Resultados que podés obtener
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-97">
            {[{
            num: '+100',
            label: 'Interpretaciones prácticas'
          }, {
            num: '+50',
            label: 'Plantillas y recursos'
          }, {
            num: '+21',
            label: 'Errores evitados'
          }, {
            num: '+10',
            label: 'Bonus incluidos'
          }].map((stat, i) => <div key={i} className="flex flex-col items-center" data-id="element-98">
                <div className="text-4xl md:text-5xl font-heading font-black text-primary mb-2 drop-shadow-[0_0_10px_rgba(212,168,87,0.2)]" data-id="element-99">
                  {stat.num}
                </div>
                <div className="text-sm md:text-base text-muted-foreground font-medium uppercase tracking-wide" data-id="element-100">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. Oferta especial */}
      <section className="py-24 relative overflow-hidden" data-id="element-101">
        <div className="absolute inset-0 pointer-events-none" data-id="element-102">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" data-id="element-103"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-104">
          <Card className="bg-card/80 backdrop-blur-xl border-primary/30 shadow-[0_0_40px_rgba(212,168,87,0.1)] overflow-hidden" data-id="element-105">
            <div className="bg-primary/10 p-6 text-center border-b border-primary/20" data-id="element-106">
              <h2 className="text-2xl font-heading font-bold text-primary mb-2" data-id="element-107">
                Oferta especial
              </h2>
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 mb-4" data-id="element-108">
                <span className="animate-pulse" data-id="element-109">🔥</span> 80% YA VENDIDO — Se
                están agotando los últimos accesos
              </div>
              <div className="space-y-2" data-id="element-110">
                <div className="flex justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-111">
                  <span data-id="element-112">ACCESOS VENDIDOS</span>
                  <span className="text-primary" data-id="element-113">80%</span>
                </div>
                <Progress value={80} className="h-2 bg-background" data-id="element-114" />
                <div className="text-xs text-muted-foreground text-right" data-id="element-115">
                  Solo queda un 20% disponible
                </div>
              </div>
            </div>

            <CardContent className="p-8 md:p-12" data-id="element-116">
              <div className="space-y-4 mb-8 text-sm md:text-base" data-id="element-117">
                <div className="text-muted-foreground font-semibold mb-4 uppercase tracking-wider text-xs" data-id="element-118">
                  Valor individual:
                </div>
                {[{
                name: 'COSMOS I',
                price: '$19.900'
              }, {
                name: 'COSMOS II',
                price: '$19.900'
              }, {
                name: 'COSMOS III',
                price: '$19.900'
              }, {
                name: 'COSMOS IV',
                price: '$19.900'
              }, {
                name: 'COSMOS V',
                price: '$19.900'
              }, {
                name: 'Bonus Exclusivos',
                price: '$29.900'
              }].map((item, i) => <div key={i} className="flex justify-between items-center border-b border-border/50 pb-2" data-id="element-119">
                    <span className="text-foreground/80" data-id="element-120">{item.name}</span>
                    <div className="flex items-center gap-3" data-id="element-121">
                      <span className="line-through text-muted-foreground/50" data-id="element-122">
                        {item.price}
                      </span>
                      <span className="text-primary font-semibold text-xs bg-primary/10 px-2 py-0.5 rounded" data-id="element-123">
                        Bonificado
                      </span>
                    </div>
                  </div>)}
              </div>

              <div className="flex justify-between items-center mb-8 p-4 bg-background rounded-lg border border-border" data-id="element-124">
                <span className="font-semibold text-muted-foreground" data-id="element-125">
                  Valor real total:
                </span>
                <span className="line-through text-xl text-muted-foreground/60" data-id="element-126">
                  $129.400
                </span>
              </div>

              <div className="text-center mb-8" data-id="element-127">
                <p className="text-sm text-primary mb-4 font-medium" data-id="element-128">
                  Hoy todos los tomos quedan bonificados comprando el principal.
                </p>
                <div className="inline-block bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4" data-id="element-129">
                  HOY ACCESO COMPLETO
                </div>
                <p className="text-muted-foreground mb-2" data-id="element-130">Hoy pagás solo</p>
                <div className="text-6xl md:text-7xl font-heading font-black text-primary mb-4 drop-shadow-[0_0_15px_rgba(212,168,87,0.3)]" data-id="element-131">
                  $19.900
                </div>
                <Badge variant="outline" className="border-green-500/50 text-green-400 bg-green-500/10 px-4 py-1.5 text-sm" data-id="element-132">
                  AHORRÁS $109.500
                </Badge>
              </div>

              <div className="text-center space-y-4" data-id="element-133">
                <p className="text-sm font-medium text-muted-foreground" data-id="element-134">
                  Pago único — Acceso de por vida
                </p>
                <Button asChild size="lg" className="w-full text-xl py-8 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(212,168,87,0.4)] transition-all hover:scale-[1.02]" data-id="element-135">
                  <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-136">
                    QUIERO ACCEDER AHORA
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 9. Opiniones de alumnas */}
      <TestimonialsMarquee data-id="element-137" />

      {/* 10. Preguntas frecuentes */}
      <section id="faq" className="py-24 relative" data-id="element-138">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-139">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-12 text-center" data-id="element-140">
            Preguntas frecuentes
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4" data-id="element-141">
            {[{
            q: '¿Qué formato tiene?',
            a: 'La colección es 100% digital. Recibirás acceso inmediato a todos los tomos en formato PDF de alta calidad, diseñados para leer en pantalla o imprimir.'
          }, {
            q: '¿Puedo verlo desde celular?',
            a: 'Sí, los materiales están optimizados para que puedas leerlos cómodamente desde tu celular, tablet o computadora en cualquier momento.'
          }, {
            q: '¿Sirve si recién comienzo?',
            a: 'Absolutamente. COSMOS I está diseñado específicamente para darte el orden mental necesario desde el día uno, evitando que te abrumes con información.'
          }, {
            q: '¿Recibo los bonus?',
            a: 'Sí, al acceder hoy recibís la colección completa (Tomos I al V) más los 6 bonus exclusivos detallados en esta página, sin costo adicional.'
          }, {
            q: '¿Obtengo acceso inmediato?',
            a: 'Sí, una vez confirmado el pago, recibirás un correo electrónico automáticamente con tus datos de acceso para descargar todo el material al instante.'
          }].map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border/50 rounded-lg px-6 data-[state=open]:border-primary/50 transition-colors" data-id="element-142">
                <AccordionTrigger className="text-left font-semibold text-lg hover:text-primary py-6" data-id="element-143">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6 leading-relaxed" data-id="element-144">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </section>

      {/* 11. Final CTA section */}
      <section className="py-32 relative overflow-hidden bg-card border-t border-border/50 text-center" data-id="element-145">
        <div className="absolute inset-0 pointer-events-none" data-id="element-146">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 rounded-t-full blur-3xl" data-id="element-147"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 max-w-3xl" data-id="element-148">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-id="element-149">
            Tu próxima interpretación comienza hoy.
          </h2>
          <p className="text-xl text-muted-foreground mb-12" data-id="element-150">
            La diferencia entre sentirte perdida y sentir claridad comienza con
            un sistema.
          </p>
          <Button size="lg" className="text-xl px-10 py-8 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(212,168,87,0.4)] transition-all hover:scale-105 w-full sm:w-auto" data-id="element-151">
            DESCARGAR COLECCIÓN COMPLETA
          </Button>
        </div>
      </section>

      {/* 12. Footer */}
      <footer className="py-8 bg-background border-t border-border/40 text-center text-muted-foreground text-sm" data-id="element-152">
        <div className="container mx-auto px-4" data-id="element-153">
          <p className="font-heading font-medium tracking-widest text-foreground/60 mb-2" data-id="element-154">
            COSMOS™
          </p>
          <p data-id="element-155">© 2026 Todos los derechos reservados</p>
        </div>
      </footer>
    </div>;
}